import { notifyCompanyRelationshipsChanged } from "../../utils/companyRelationshipCounts";
// src/components/HiringTeamCard.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography, Divider, IconButton, TextField, Button } from "@mui/material";
import { Trash } from "iconsax-react";
import {
  getDefaultContactRoleLabel,
  normalizeContactRoleLabel,
} from "../../utils/contactRoles";
import JobOwnerDialog from "../JobOwnerDialog";
import {
  SIDEBAR_WIDGET_BORDER_COLOR,
  SIDEBAR_WIDGET_CARD_SX,
  SIDEBAR_WIDGET_TITLE_SX,
} from "./sidebarWidgetStyles";

const BG = "#FFFFFF";          // ✅ white background (card + inside)
const BORDER = SIDEBAR_WIDGET_BORDER_COLOR;
const TEXT = "#344054";
const MUTED = "#A2ABB4";
const PURPLE = "#9C4FFF";
const ACTION_BUTTON_SX = {
  backgroundColor: PURPLE,
  color: "#FFFFFF",
  borderRadius: 999,
  textTransform: "none",
  fontFamily: "Be Vietnam Pro",
  fontWeight: 400,
  boxShadow: "none",
  height: 34,
  minWidth: 88,
  "&:hover": {
    backgroundColor: "#7a32e1",
  },
};

const TEXT_ACTION_BUTTON_SX = {
  color: PURPLE,
  textTransform: "none",
  fontFamily: "Be Vietnam Pro",
  fontWeight: 400,
  minWidth: 0,
  padding: 0,
  margin: 0,
  "&:hover": {
    backgroundColor: "transparent",
    opacity: 0.9,
  },
};

const DELETE_ACTION_BUTTON_SX = {
  p: 0.5,
  width: 34,
  height: 34,
  borderRadius: 999,
  backgroundColor: "transparent",
  color: PURPLE,
  "&:hover": {
    backgroundColor: "rgba(156, 79, 255, 0.08)",
  },
  "&.Mui-disabled": {
    color: "rgba(156, 79, 255, 0.4)",
  },
};

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const uniqTextValues = (values = []) =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );

function getToken(explicitToken) {
  return (
    explicitToken ||
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("jwt") ||
    sessionStorage.getItem("accessToken") ||
    sessionStorage.getItem("access_token") ||
    ""
  );
}

function normalizeMemberRow(row) {
  if (!row || typeof row !== "object") return null;

  const name = String(
    row.name || row.hiring_manager_name || row.contact_name || row.user_name || ""
  ).trim();
  if (!name) return null;

  const role = normalizeContactRoleLabel(
    row.role || row.hiring_manager_role || row.position || row.title || row.user_role,
    {
      contactKind: row.contact_kind,
      source: row.source,
      fallback: getDefaultContactRoleLabel({
        contactKind: row.contact_kind,
        source: row.source,
        fallback: "Hiring Manager",
      }),
    }
  );

  const id = row.hiring_manager_id ?? row.id ?? null;

  return {
    id,
    name,
    role,
    source: String(row.source || "").trim(),
    contact_kind: String(row.contact_kind || "").trim().toLowerCase(),
  };
}

function isHiringManagerMember(row) {
  const contactKind = String(row?.contact_kind || "").trim().toLowerCase();
  if (contactKind.includes("recruiter")) return false;
  if (contactKind === "contact" || contactKind.includes("team_member")) return false;
  if (contactKind.includes("hiring_manager")) return true;

  const source = String(row?.source || "").trim().toLowerCase();
  const role = String(row?.role || "").trim().toLowerCase();
  if (source === "ai team scan" || role === "team member") return false;
  return true;
}

function normalizeEntityType(value) {
  return String(value || "").trim().toLowerCase();
}

function getCardTitle(entityType) {
  const normalized = normalizeEntityType(entityType);
  if (["lead", "company", "client"].includes(normalized)) {
    return "Hiring Manager";
  }
  return "Hiring Team";
}

function getDefaultMemberRole(entityType) {
  return getCardTitle(entityType) === "Hiring Manager" ? "Hiring Manager" : "Hiring Team Member";
}

async function requestJson({ url, method = "GET", token, body }) {
  const headers = {};
  const authToken = getToken(token);
  const cleanedToken = authToken ? String(authToken).trim().replace(/^Bearer\s+/i, "") : "";
  if (cleanedToken) {
    headers.Authorization = `Bearer ${cleanedToken}`;
    headers["x-access-token"] = cleanedToken;
  }
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  const res = await fetch(url, {
    method,
    headers,
    credentials: "include",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const data = await res.json().catch(() => ({}));
  return { res, data };
}

function decodeJwt(token) {
  try {
    const payload = String(token).split(".")[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function getCurrentRole() {
  const token = getToken();
  const decoded = decodeJwt(token);
  let role = decoded?.role || localStorage.getItem("role") || "";

  try {
    if (!role) {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        role = parsedUser?.role || "";
      }
    }
  } catch {
    // ignore malformed stored user
  }

  return String(role || "").toLowerCase();
}

async function fetchHiringManagers({ apiBase, entityType, entityId }) {
  if (!entityType || entityId === undefined || entityId === null || entityId === "") {
    return [];
  }

  const base = apiBase || API_BASE || "";
  const url = `${base}/api/hiring-managers?entityType=${encodeURIComponent(
    String(entityType).trim().toLowerCase()
  )}&entityId=${encodeURIComponent(String(entityId))}`;

  const { res, data } = await requestJson({ url, method: "GET" });
  if (!res.ok) {
    throw new Error(data?.error || data?.message || `Failed to load hiring team (${res.status})`);
  }

  const rows = Array.isArray(data?.rows) ? data.rows : [];
  return rows
    .map(normalizeMemberRow)
    .filter(Boolean)
    .filter(isHiringManagerMember)
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

export default function HiringTeamCard({
  className,
  sx = {},
  apiBase,
  entityType,
  entityId,
  members: membersProp,
  source: sourceProp = "LinkedIn",
  summaryItems = [],
  onSourceChange,
  onDeleteMember,
  onAddNew,
  onEditAll,
}) {
  const normalizedMembersProp = useMemo(
    () => (Array.isArray(membersProp) ? membersProp : []),
    [membersProp]
  );
  const hasEntity = Boolean(entityType && entityId !== undefined && entityId !== null && entityId !== "");
  const cardTitle = getCardTitle(entityType);
  const defaultMemberRole = getDefaultMemberRole(entityType);
  const [members, setMembers] = useState(normalizedMembersProp);
  const [source, setSource] = useState(sourceProp);
  const [loading, setLoading] = useState(hasEntity);
  const [processing, setProcessing] = useState(false);
  const [initialLoad, setInitialLoad] = useState(hasEntity);
  const [error, setError] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editedMembers, setEditedMembers] = useState({});
  const normalizedSummaryItems = useMemo(() => uniqTextValues(summaryItems), [summaryItems]);
  const memberNameSuggestions = useMemo(
    () => uniqTextValues(members.map((member) => member?.name)),
    [members]
  );
  const memberRoleSuggestions = useMemo(
    () => uniqTextValues([defaultMemberRole, ...members.map((member) => member?.role)]),
    [defaultMemberRole, members]
  );

  const loadMembers = useCallback(async () => {
    if (!hasEntity) {
      setMembers(normalizedMembersProp);
      setError("");
      setLoading(false);
      setInitialLoad(false);
      return;
    }

    if (initialLoad) {
      setLoading(true);
    }
    setError("");

    try {
      const nextMembers = await fetchHiringManagers({ apiBase, entityType, entityId });
      setMembers(nextMembers);
    } catch (err) {
      setError(err?.message || "Unable to load hiring team.");
    } finally {
      if (initialLoad) {
        setLoading(false);
        setInitialLoad(false);
      }
    }
  }, [apiBase, entityId, entityType, hasEntity, initialLoad, normalizedMembersProp]);

  useEffect(() => {
    setSource(sourceProp ?? "");
  }, [sourceProp]);

  useEffect(() => {
    setInitialLoad(hasEntity);
  }, [entityType, entityId, hasEntity]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  useEffect(() => {
    if (editMode) {
      const nextEdits = {};
      members.forEach((member) => {
        if (member?.id) {
          nextEdits[member.id] = { name: member.name || "", role: member.role || "" };
        }
      });
      setEditedMembers(nextEdits);
    }
  }, [editMode, members]);

  const role = getCurrentRole();
  const canManageHiringTeam = ["recruiter", "administrator", "admin", "system_user"].includes(role);

  const toggleEditMode = useCallback(() => {
    if (!canManageHiringTeam) return;
    if (editMode) {
      setEditMode(false);
      setEditedMembers({});
      onEditAll?.(false);
      loadMembers();
    } else {
      setEditMode(true);
      setAddDialogOpen(false);
      onEditAll?.(true);
    }
  }, [canManageHiringTeam, editMode, loadMembers, onEditAll]);

  const handleStartAdd = useCallback(() => {
    if (!canManageHiringTeam || !hasEntity) return;
    setEditMode(false);
    setError("");
    onAddNew?.();
    setAddDialogOpen(true);
  }, [canManageHiringTeam, hasEntity, onAddNew]);

  const handleCloseAddDialog = useCallback(() => {
    if (processing) return;
    setAddDialogOpen(false);
  }, [processing]);

  const handleSaveNewMember = useCallback(async ({ ownerName, ownerRole } = {}) => {
    if (!canManageHiringTeam || !hasEntity) {
      const nextError = "Unable to save hiring team member from this view.";
      setError(nextError);
      throw new Error(nextError);
    }
    const name = String(ownerName || "").trim();
    const roleValue = String(ownerRole || defaultMemberRole).trim() || defaultMemberRole;
    const entityTypeValue = String(entityType || "").trim().toLowerCase();
    const entityIdValue = Number(entityId);

    if (!name) {
      const nextError = "Enter a member name before saving.";
      setError(nextError);
      throw new Error(nextError);
    }
    if (!entityTypeValue || Number.isNaN(entityIdValue) || entityIdValue <= 0) {
      const nextError = "Unable to save hiring team member: invalid entity.";
      setError(nextError);
      throw new Error(nextError);
    }

    setProcessing(true);
    setError("");

    try {
      const body = {
        entityType: entityTypeValue,
        entityId: entityIdValue,
        name,
        role: roleValue,
        source: source || null,
      };
      const base = apiBase || API_BASE || "";
      const url = `${base}/api/hiring-managers`;
      const { res, data } = await requestJson({ url, method: "POST", body });
      if (!res.ok) {
        throw new Error(data?.error || data?.message || `Failed to add hiring team member (${res.status})`);
      }
      await loadMembers();
    } catch (err) {
      setError(err?.message || "Unable to add hiring team member.");
      throw err;
    } finally {
      setProcessing(false);
    }
  }, [
    apiBase,
    canManageHiringTeam,
    defaultMemberRole,
    entityId,
    entityType,
    hasEntity,
    loadMembers,
    source,
  ]);

  const handleEditFieldChange = useCallback((memberId, field, value) => {
    setEditedMembers((prev) => ({
      ...prev,
      [memberId]: {
        ...(prev[memberId] || {}),
        [field]: value,
      },
    }));
  }, []);

  const handleSaveMember = useCallback(
    async (member) => {
      if (!canManageHiringTeam || !member?.id) return;
      const edited = editedMembers[member.id] || {};
      const name = String(edited.name || member.name || "").trim();
      const roleValue = String(edited.role || member.role || defaultMemberRole).trim() || defaultMemberRole;
      if (!name) {
        setError("Enter a name before saving.");
        return;
      }

      setProcessing(true);
      setError("");

      try {
        const base = apiBase || API_BASE || "";
        const url = `${base}/api/hiring-managers/${encodeURIComponent(String(member.id))}`;
        const { res, data } = await requestJson({
          url,
          method: "PUT",
          body: {
            name,
            role: roleValue,
            source: source || null,
          },
        });
        if (!res.ok) {
          throw new Error(data?.error || data?.message || `Failed to save hiring team member (${res.status})`);
        }
        setEditedMembers((prev) => {
          const next = { ...prev };
          delete next[member.id];
          return next;
        });
        await loadMembers();
      } catch (err) {
        setError(err?.message || "Unable to save hiring team member.");
      } finally {
        setProcessing(false);
      }
    },
    [apiBase, canManageHiringTeam, defaultMemberRole, editedMembers, loadMembers, source]
  );

  const handleDeleteMember = useCallback(
    async (member) => {
      if (!canManageHiringTeam) return;
      if (typeof onDeleteMember === "function") {
        return onDeleteMember(member);
      }
      if (!entityType || entityId === undefined || entityId === null || entityId === "") {
        return;
      }
      if (!member?.id) return;

      if (!window.confirm(`Delete ${member.name} from hiring team?`)) {
        return;
      }

      setProcessing(true);
      setError("");

      try {
        const base = apiBase || API_BASE || "";
        const url = `${base}/api/hiring-managers/${encodeURIComponent(String(member.id))}`;
        const { res, data } = await requestJson({ url, method: "DELETE" });
        if (!res.ok) {
          throw new Error(data?.error || data?.message || `Failed to delete hiring team member (${res.status})`);
        }
        notifyCompanyRelationshipsChanged();
        await loadMembers();
      } catch (err) {
        setError(err?.message || "Unable to delete hiring team member.");
      } finally {
        setProcessing(false);
      }
    },
    [apiBase, entityId, entityType, loadMembers, onDeleteMember, canManageHiringTeam]
  );

  return (
    <>
      <Box
        className={className || ""}
        sx={{
          ...SIDEBAR_WIDGET_CARD_SX,
          backgroundColor: BG,
          ...sx,
        }}
      >
        <Box sx={{ p: 4, backgroundColor: BG }}>
        <Typography
          sx={{
            ...SIDEBAR_WIDGET_TITLE_SX,
            mb: 4,
          }}
        >
          {cardTitle}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", pb: 1.2 }}>
          <Typography
            sx={{
              flex: 1,
              color: MUTED,
              fontFamily: "Be Vietnam Pro",
              fontSize: 12,
              fontWeight: 400,
            }}
          >
            Name
          </Typography>
          <Typography
            sx={{
              width: 160,
              color: MUTED,
              fontFamily: "Be Vietnam Pro",
              fontSize: 12,
              fontWeight: 400,
            }}
          >
            Role
          </Typography>
          <Typography
            sx={{
              width: 70,
              textAlign: "right",
              color: MUTED,
              fontFamily: "Be Vietnam Pro",
              fontSize: 12,
              fontWeight: 400,
            }}
          >
            Actions
          </Typography>
        </Box>

        <Divider sx={{ borderColor: BORDER }} />

        {loading && members.length === 0 ? (
          <Box sx={{ py: 3 }}>
            <Typography sx={{ color: MUTED, fontFamily: "Be Vietnam Pro", fontSize: 13 }}>
              Loading hiring team...
            </Typography>
          </Box>
        ) : (
          <>
            {members.length > 0 ? (
              members.map((m, idx) => {
                const isEditingRow = editMode && Boolean(editedMembers[m.id]);
                return (
                  <Box key={m.id ?? `${m.name}-${idx}`}>
                    {isEditingRow ? (
                      <Box
                        sx={{
                          py: 1.2,
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <TextField
                          value={editedMembers[m.id]?.name || ""}
                          onChange={(event) => handleEditFieldChange(m.id, "name", event.target.value)}
                          placeholder="Name"
                          fullWidth
                          size="small"
                        />
                        <TextField
                          value={editedMembers[m.id]?.role || ""}
                          onChange={(event) => handleEditFieldChange(m.id, "role", event.target.value)}
                          placeholder="Role"
                          fullWidth
                          size="small"
                        />
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: 0.5,
                            pt: 0.25,
                          }}
                        >
                          <Button
                            sx={ACTION_BUTTON_SX}
                            size="small"
                            onClick={() => handleSaveMember(m)}
                            disabled={processing}
                          >
                            Save
                          </Button>
                          {canManageHiringTeam ? (
                            <IconButton
                              onClick={() => handleDeleteMember(m)}
                              size="small"
                              aria-label={`Delete ${m.name}`}
                              disabled={processing}
                              sx={DELETE_ACTION_BUTTON_SX}
                            >
                              <Trash size={23} color={PURPLE} variant="Outline" />
                            </IconButton>
                          ) : null}
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          py: 1.2,
                          gap: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            color: TEXT,
                            fontFamily: "Be Vietnam Pro",
                            fontSize: 14,
                            fontWeight: 400,
                          }}
                          noWrap
                        >
                          {m.name}
                        </Typography>
                        <Typography
                          sx={{
                            width: 160,
                            color: TEXT,
                            fontFamily: "Be Vietnam Pro",
                            fontSize: 14,
                            fontWeight: 400,
                          }}
                          noWrap
                        >
                          {m.role}
                        </Typography>

                        <Box
                          sx={{
                            width: 70,
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 0.5,
                          }}
                        >
                          {canManageHiringTeam ? (
                            <IconButton
                              onClick={() => handleDeleteMember(m)}
                              size="small"
                              aria-label={`Delete ${m.name}`}
                              disabled={processing}
                              sx={DELETE_ACTION_BUTTON_SX}
                            >
                              <Trash size={23} color={PURPLE} variant="Outline" />
                            </IconButton>
                          ) : null}
                        </Box>
                      </Box>
                    )}
                    {idx !== members.length - 1 && <Divider sx={{ borderColor: BORDER }} />}
                  </Box>
                );
              })
            ) : (
              <Box sx={{ py: 3 }}>
                <Typography sx={{ color: MUTED, fontFamily: "Be Vietnam Pro", fontSize: 13 }}>
                  No hiring team members yet.
                </Typography>
              </Box>
            )}

            {processing ? (
              <Box sx={{ py: 1 }}>
                <Typography sx={{ color: MUTED, fontFamily: "Be Vietnam Pro", fontSize: 13 }}>
                  Updating hiring team...
                </Typography>
              </Box>
            ) : null}
          </>
        )}

        {error ? (
          <Typography sx={{ color: "#DC2626", fontFamily: "Be Vietnam Pro", fontSize: 12, mt: 1.5 }}>
            {error}
          </Typography>
        ) : null}

        <Box sx={{ mt: 2.2 }}>
          <Typography
            sx={{
              color: TEXT,
              fontFamily: "Be Vietnam Pro",
              fontSize: 13,
              fontWeight: 400,
              mb: 1,
            }}
          >
            Source
          </Typography>

          <TextField
            value={source}
            onChange={(e) => {
              const next = e.target.value;
              setSource(next);
              onSourceChange?.(next);
            }}
            fullWidth
            placeholder="LinkedIn"
            disabled={!canManageHiringTeam}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                height: 52,
                backgroundColor: "#fff",
                "& fieldset": { borderColor: "#D1D5DB" },
                "&:hover fieldset": { borderColor: "#CBD5E1" },
                "&.Mui-focused fieldset": {
                  borderColor: "#A78BFA",
                  borderWidth: 2,
                },
              },
              "& input": {
                fontFamily: "Be Vietnam Pro",
                fontSize: 14,
                color: TEXT,
                fontWeight: 400,
              },
            }}
          />
        </Box>

        <Box
          sx={{
            mt: 2.6,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1.2,
          }}
        >
          {canManageHiringTeam ? (
            <>
              <Button
                sx={{
                  ...TEXT_ACTION_BUTTON_SX,
                  opacity: hasEntity ? 1 : 0.5,
                  cursor: hasEntity ? "pointer" : "default",
                }}
                size="small"
                onClick={handleStartAdd}
                disabled={!hasEntity || processing}
              >
                Add New
              </Button>
              <Typography
                sx={{
                  color: PURPLE,
                  fontFamily: "Be Vietnam Pro",
                  fontSize: 13,
                  fontWeight: 400,
                  userSelect: "none",
                }}
              >
                |
              </Typography>
              <Button sx={TEXT_ACTION_BUTTON_SX} size="small" onClick={toggleEditMode} disabled={processing}>
                {editMode ? "Cancel" : "Edit"}
              </Button>
            </>
          ) : (
            <Typography
              sx={{
                color: MUTED,
                fontFamily: "Be Vietnam Pro",
                fontSize: 13,
                fontWeight: 400,
              }}
            >
              Only recruiters and administrators can manage this hiring team.
            </Typography>
          )}
        </Box>
      </Box>
      </Box>
      <JobOwnerDialog
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
        onSubmit={handleSaveNewMember}
        apiBase={apiBase || API_BASE}
        resetKey={`${entityType || "entity"}:${entityId || "new"}:hiring-manager`}
        title={cardTitle === "Hiring Manager" ? "Add Hiring Manager" : "Add Hiring Team Member"}
        submitLabel={
          cardTitle === "Hiring Manager" ? "Add New Hiring Manager" : "Add New Team Member"
        }
        sectionTitle="Details"
        summaryItems={normalizedSummaryItems}
        defaultOwnerNames={[]}
        defaultOwnerRoles={[defaultMemberRole]}
        nameSuggestions={memberNameSuggestions}
        roleSuggestions={memberRoleSuggestions}
        namePlaceholder={
          cardTitle === "Hiring Manager" ? "Select hiring manager" : "Select team member"
        }
        rolePlaceholder="Select role"
        roleChipsBelow
        requiredNameErrorText={
          cardTitle === "Hiring Manager"
            ? "Select a hiring manager name to continue."
            : "Select a team member name to continue."
        }
        requiredRoleErrorText="Select a role to continue."
        loadRecruiterNameOptions={false}
      />
    </>
  );
}
